'use client'

import { memo, useMemo } from 'react'
import { IconSwitch3 } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { pathOr } from 'ramda'

import { AddToCartButton, SubstitutesModal } from 'modules/cart'
import {
  AddToFavouritesButton,
  getProductPrices,
  getProductUnitDetails,
  ProductSpecialPromotion,
  ProductTypes
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import Button from 'shared/ui/Button'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import styles from './SpecialPromotionItem.module.scss'

const SpecialPromotionItem = memo(
  ({
    product,
    seoButtonPlace,
    idxOnList,
    listName
  }: {
    product: ProductTypes
    seoButtonPlace: string
    idxOnList: number
    listName: SeoListName
  }) => {
    const promoPrice = useMemo(() => {
      return product.specialOfferPricing?.priceGross?.formatted || null
    }, [product.specialOfferPricing])

    const defaultPrice = useMemo(() => {
      return getProductPrices(product).defaultPrice
    }, [product])

    const unitDetails = useMemo(() => {
      return getProductUnitDetails(product)
    }, [product])

    const productPath = product?.slug || ''
    const formattedProductPath =
      productPath[0] !== '/' ? `/${productPath}` : productPath

    const productImage = useMemo(() => {
      return pathOr(
        '/static/photo-placeholder.svg',
        ['files', 0, 'thumbnailPathSmall'],
        product
      )
    }, [product])

    return (
      <div className={styles.wrapper}
        id={product.id}
        key={`special-promo-product-card-${product.id}-${idxOnList}`}
      >
        <div className={styles.iconSectionWrapper}>
          <AddToFavouritesButton
            product={product}
            idxOnList={idxOnList}
            listName={listName}
            seoButtonPlace={seoButtonPlace}
          />
        </div>
        <Image src={productImage} alt={product.name} width={78} height={78} />
        <div className={styles.insideWrapper}>
          <div className={styles.productDetails}>
            <div className={styles.producerName}>{product?.producer?.name}</div>
            <Link className={styles.productName} href={formattedProductPath}>
              {product.name}
            </Link>
            <div className={styles.productUnitDetails}>{unitDetails}</div>
            <ProductSpecialPromotion product={product} notClickable />
          </div>
          {product.isAvailable ? (
            <div className={styles.availability}>
              <div className={styles.availabilityWrapper}>
                {isNotNilOrEmpty(promoPrice) ? (
                  <div className={styles.priceWrapper}>
                    <div className={styles.oldPrice}
                      id='cart_default_price'
                      dangerouslySetInnerHTML={{ __html: defaultPrice }}
                    />
                    <div className={styles.currentPrice}
                      id='cart_promo_price'
                      dangerouslySetInnerHTML={{ __html: promoPrice || '' }}
                    />
                  </div>
                ) : (
                  <div className={styles.priceWrapper}>
                    <div className={styles.currentPrice}
                      id='cart_current_price'
                      dangerouslySetInnerHTML={{ __html: defaultPrice }}
                    />
                  </div>
                )}
                <div className={clsx(styles.addButtonWrapper, 'cart-product-buttons')}>
                  <AddToCartButton
                    variant='primaryOutline'
                    product={product}
                    quantityJump={1}
                    seoButtonPlace={seoButtonPlace}
                    listName={listName}
                    idxOnList={idxOnList}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <p className={styles.productAvailabilityParagraph}>
                  Produkt niedostępny
                </p>
                <SubstitutesModal
                  trigger={
                    <Button
                      id='cart_show_replacements_button'
                      startIcon={<IconSwitch3 />}
                      variant='primaryOutline'
                    >
                      Pokaż zamienniki
                    </Button>
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    )
  }
)

export default SpecialPromotionItem
